import React, { useCallback, useEffect, useRef, useState } from 'react';

import Bookmark from '@components/Bookmark';
import Button from '@components/Button';
import BylineWithHeadshot from '@components/BylineWithHeadshot';
import ClientRenderedContent from '@components/ClientRenderedContent';
import ListenButton from '@components/ListenButton';
import { ONBOARDING_STEP_IDS } from '@components/UserOnboardingTooltip/constants';
import { registerOnboardingAnchor } from '@components/UserOnboardingTooltip/utils';
import { useAppContext } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import useNativeAppVersionCheck from '@hooks/useNativeAppVersionCheck';
import type { Author } from '@interfaces/authors';
import { ClickTarget, ContentType, click } from '@tracking';

import ArticleCommentJumpLink from './ArticleCommentJumpLink';
import ShareArticleModal from './ShareArticleModal';

interface DetailsProps {
  articleId: number;
  audioActive: boolean;
  authors: Author[];
  publishedAt: string;
  commentsCount?: number;
  commenters?: string[];
  canShareArticle: boolean;
  setAudioActive: (audioActive: boolean) => void;
}

const STICKY_NAV_PLUS_SPACE = 100;

const ArticleDetails = ({
  articleId,
  authors,
  publishedAt,
  commentsCount = 0,
  commenters = [],
  canShareArticle,
  audioActive,
  setAudioActive,
}: DetailsProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const shareButtonRef = useRef<HTMLDivElement | null>(null);
  const { initialCurrentUser } = useAppContext();
  const { isApp3_0OrGreater, isNativeApp } = useNativeAppVersionCheck();
  const showListenButton =
    initialCurrentUser?.is_subscriber && (!isNativeApp || (isNativeApp && isApp3_0OrGreater));
  const { trackEvent } = useEventTracking();

  useEffect(() => {
    const el = shareButtonRef.current;
    if (canShareArticle && !isNativeApp && el) {
      registerOnboardingAnchor(ONBOARDING_STEP_IDS.ArticleShare, el);
      return () => registerOnboardingAnchor(ONBOARDING_STEP_IDS.ArticleShare, null);
    }
  }, [canShareArticle, isNativeApp]);

  const handleShareClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.ShareButton }));
    setIsShareModalOpen(true);
  };

  const handleCommentJumpClick = useCallback(() => {
    const commentsSection = document.getElementById('comments-section');
    if (!commentsSection) return;
    const scrollTopDistance = commentsSection.getBoundingClientRect().top;
    window?.scrollBy({
      behavior: 'smooth',
      left: 0,
      top: scrollTopDistance - STICKY_NAV_PLUS_SPACE,
    });
  }, []);

  return (
    <>
      <div className="grid w-full grid-cols-[1fr_auto] items-start gap-x-12 [grid-template-areas:'byline_byline'_'divider_divider'_'time_actions'_'comments_comments'] md:[grid-template-areas:'byline_actions'_'divider_divider'_'time_comments']">
        {authors.length > 0 && (
          <div className="[grid-area:byline]">
            <BylineWithHeadshot
              authors={authors}
              authorClassName="author-name-body dark:text-white"
              className="max-w-sm"
            />
          </div>
        )}

        <div
          aria-hidden="true"
          className="mb-10 mt-12 border-0 border-b border-solid border-grayscale-beige [grid-area:divider]"
        />

        <ClientRenderedContent>
          <time
            className="mb-0 block font-suisse-intl-mono text-overline-sm font-normal text-grayscale-gray [grid-area:time] sm:text-overline-md lg:text-overline-sm xl:text-overline-md"
            dateTime={publishedAt}
          >
            {publishedAt}
          </time>

          {commentsCount > 0 && (
            <div className="[grid-area:comments]">
              <ArticleCommentJumpLink
                commentsCount={commentsCount}
                commenters={commenters}
                onClick={handleCommentJumpClick}
              />
            </div>
          )}
        </ClientRenderedContent>

        <div className="flex shrink-0 items-end gap-12 justify-self-end [grid-area:actions]">
          {showListenButton && (
            <ListenButton
              contentType={ContentType.Article}
              isAudioActive={audioActive}
              setIsAudioActive={setAudioActive}
            />
          )}
          {canShareArticle && !isNativeApp && (
            <div ref={shareButtonRef} className="ml-auto" data-onboarding-anchor="article-share">
              <Button
                as="button"
                className="share-button flex items-center rounded-20 border border-solid border-grayscale-gray bg-white px-12 py-8 font-suisse-intl-mono text-button-xs text-grayscale-gray outline-none hover:transition-all sm:hover:border-primary-magenta sm:hover:bg-primary-magenta sm:hover:text-white dark:bg-[var(--light-grayscale-charcoal)] dark:text-white sm:dark:hover:border-white sm:dark:hover:text-white print:hidden"
                onClick={handleShareClick}
              >
                Share
              </Button>
            </div>
          )}
          <Bookmark contentId={articleId} contentType={ContentType.Article} />
        </div>
      </div>
      {canShareArticle && !isNativeApp && (
        <ShareArticleModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          articleId={articleId}
        />
      )}
    </>
  );
};

export default ArticleDetails;
